import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getKid, archiveKid } from '../../lib/api/kids';
import { getLedger } from '../../lib/api/ledger';
import Button from '../../components/common/Button';
import SafeIcon from '../../common/SafeIcon';
import LedgerItem from './LedgerItem';
import AddEntryModal from './AddEntryModal';

export default function KidDetail() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const { execute: fetchKid } = useApi(getKid);
  const { execute: fetchLedger, loading: loadingLedger } = useApi(getLedger);
  const { execute: execArchive } = useApi(archiveKid);

  const [kid, setKid] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    // Wait until auth and ID are actually present
    if (!id || authLoading || !user?.id) return;

    try {
      setInitialLoading(true);
      setError(null);
      
      // Fetch kid details (triggers allowance sync)
      const kidData = await fetchKid(id, user.id, user.householdId);
      if (!kidData) {
        throw new Error("Kid profile not found.");
      }
      setKid(kidData);
      
      // Fetch transaction history
      const entries = await fetchLedger(id);
      setLedger(entries || []);
    } catch(e) {
      console.error("Error loading kid detail:", e);
      setError(e.message || "Could not load profile. Please try refreshing.");
    } finally {
      setInitialLoading(false);
    }
  }, [id, user?.id, user?.householdId, authLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleArchive = async () => {
    if (!window.confirm(`Are you sure you want to archive this profile?`)) return;
    try {
      await execArchive(kid.id);
      navigate('/');
    } catch (e) {
      console.error("Archive failed", e);
    }
  };

  // Determine if we should show the spinner
  if (initialLoading || authLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
        <p className="text-gray-500 font-medium animate-pulse">Loading secure profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="bg-red-50 p-4 rounded-2xl mb-6">
          <SafeIcon icon="AlertCircle" className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 font-bold">{error}</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => navigate('/')}>Back to Dashboard</Button>
          <Button onClick={loadData}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!kid) return null;

  const currentBalance = Number(kid.balance || 0);
  const isNegative = currentBalance < 0;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 pb-20">
      <button 
        onClick={() => navigate('/')} 
        className="group text-sm font-bold text-gray-400 hover:text-indigo-600 flex items-center transition-colors px-1"
      >
        <SafeIcon icon="ArrowLeft" className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> 
        Back to Family
      </button>

      <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center space-x-5">
          <div className="w-20 h-20 bg-indigo-600 rounded-3xl flex items-center justify-center text-white text-3xl font-black shadow-xl shadow-indigo-100">
            {kid.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">{kid.name}</h1>
            <div className="flex items-center mt-1">
              <span className={`text-xl font-black ${isNegative ? 'text-red-500' : 'text-green-600'}`}>
                {isNegative ? '-' : ''}${Math.abs(currentBalance).toFixed(2)}
              </span>
              <span className="mx-2 text-gray-300">•</span>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Available Balance</span>
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="secondary" onClick={handleArchive} icon="Archive" className="rounded-xl">Archive</Button>
          <Button onClick={() => setShowAdd(true)} icon="Plus" className="shadow-lg shadow-indigo-100 rounded-xl">Add Entry</Button>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
          <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Transaction History</h3>
          <span className="text-[10px] bg-white px-2.5 py-1 rounded-full border border-gray-200 text-gray-500 font-bold uppercase">
            {ledger.length} Entries
          </span>
        </div>
        
        <div className="divide-y divide-gray-50">
          {loadingLedger && ledger.length === 0 ? (
            <div className="p-12 text-center text-gray-400 font-medium">Updating ledger...</div>
          ) : ledger.length === 0 ? (
            <div className="p-24 text-center">
              <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon="FileText" className="text-gray-300 w-8 h-8" />
              </div>
              <p className="text-gray-400 font-medium">No transactions yet.</p>
            </div>
          ) : (
            ledger.map(entry => (
              <LedgerItem key={entry.id} entry={entry} onUpdate={loadData} />
            ))
          )}
        </div>
      </div>

      {showAdd && (
        <AddEntryModal 
          kidId={kid.id} 
          onClose={() => setShowAdd(false)} 
          onSuccess={loadData} 
        />
      )}
    </div>
  );
}